/**
 * Notification Service for HR Module
 * Handles browser notifications for shift reminders, leave updates, and streak milestones
 */

// Safely detect whether the Notification Web API exists. iOS Safari and some
// embedded webviews don't ship this API, so referencing `Notification`
// unguarded throws a ReferenceError and crashes the entire React app.
const _notifAvailable =
  typeof window !== 'undefined' && typeof window.Notification !== 'undefined';

class NotificationService {
  constructor() {
    this.permission = _notifAvailable ? window.Notification.permission : 'default';
    this.swRegistration = null;
  }

  /**
   * Check if notifications are supported
   */
  isSupported() {
    return _notifAvailable && 'serviceWorker' in navigator;
  }

  /**
   * Request notification permission
   */
  async requestPermission() {
    if (!this.isSupported()) {
      console.warn('Notifications not supported');
      return false;
    }

    try {
      const permission = await window.Notification.requestPermission();
      this.permission = permission;
      return permission === 'granted';
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  }

  /**
   * Check if permission is granted
   */
  hasPermission() {
    return this.permission === 'granted';
  }

  /**
   * Resolve the app's root service worker registration for notifications.
   *
   * Jul 2026 fix: this used to register `/notification-sw.js` at the SAME
   * root scope as `/sw.js` (registered by index.html). The two script URLs
   * replaced each other on every page load, and since both call
   * skipWaiting()+clients.claim(), each swap fired `controllerchange` in
   * EVERY open tab → all tabs force-reloaded whenever a new tab opened.
   * `/sw.js` already handles push/notificationclick, so we simply reuse it.
   */
  async registerServiceWorker() {
    if (!('serviceWorker' in navigator)) {
      return null;
    }

    try {
      const existing = await navigator.serviceWorker.getRegistration('/');
      if (existing) {
        this.swRegistration = existing;
        return existing;
      }
      // `.ready` pends forever if registration failed — cap the wait.
      this.swRegistration = await Promise.race([
        navigator.serviceWorker.ready,
        new Promise((resolve) => setTimeout(() => resolve(null), 5000)),
      ]);
      return this.swRegistration;
    } catch (error) {
      console.error('Service Worker lookup failed:', error);
      return null;
    }
  }

  /**
   * Show a notification
   */
  async showNotification(title, options = {}) {
    if (!this.hasPermission()) {
      console.warn('Notification permission not granted');
      return null;
    }

    const defaultOptions = {
      icon: '/logo192.png',
      badge: '/logo192.png',
      vibrate: [200, 100, 200],
      requireInteraction: false,
      silent: false,
      ...options
    };

    try {
      // Use service worker notification if available
      if (this.swRegistration) {
        return await this.swRegistration.showNotification(title, defaultOptions);
      }
      
      // Fallback to regular notification
      if (!_notifAvailable) return null;
      return new window.Notification(title, defaultOptions);
    } catch (error) {
      console.error('Error showing notification:', error);
      return null;
    }
  }

  /**
   * Show shift reminder notification
   */
  async showShiftReminder(shiftTime, minutesBefore = 30) {
    return this.showNotification('Shift Starting Soon', {
      body: `Your shift starts at ${shiftTime}. Please mark your attendance.`,
      tag: 'shift-reminder',
      icon: '/logo192.png',
      data: { type: 'shift_reminder', action: '/hr/mark-attendance' },
      actions: [
        { action: 'mark-attendance', title: 'Mark Attendance' },
        { action: 'dismiss', title: 'Dismiss' }
      ]
    });
  }

  /**
   * Show late attendance notification
   */
  async showLateAttendanceAlert() {
    return this.showNotification('Attendance Not Marked', {
      body: 'You have not marked attendance today. Please check in immediately to avoid being marked absent.',
      tag: 'late-attendance',
      icon: '/logo192.png',
      requireInteraction: true,
      data: { type: 'late_attendance', action: '/hr/mark-attendance' }
    });
  }

  /**
   * Show leave request notification (for admin)
   */
  async showLeaveRequestNotification(employeeName, leaveType, days) {
    return this.showNotification('New Leave Request', {
      body: `${employeeName} has requested ${days} days of ${leaveType} leave.`,
      tag: `leave-request-${Date.now()}`,
      icon: '/logo192.png',
      data: { type: 'leave_request', action: '/hr/leaves' },
      actions: [
        { action: 'view', title: 'View Request' },
        { action: 'dismiss', title: 'Later' }
      ]
    });
  }

  /**
   * Show leave approved/rejected notification (for employee)
   */
  async showLeaveStatusNotification(status, leaveType) {
    const isApproved = status === 'approved';
    return this.showNotification(`Leave ${isApproved ? 'Approved' : 'Rejected'}`, {
      body: `Your ${leaveType} leave request has been ${status}.`,
      tag: `leave-status-${Date.now()}`,
      icon: '/logo192.png',
      data: { type: 'leave_status', action: '/hr/leaves' }
    });
  }

  /**
   * Show streak milestone notification
   */
  async showStreakMilestoneNotification(streakDays, rewardDescription) {
    return this.showNotification('Attendance Streak Achievement!', {
      body: `Congratulations! You achieved a ${streakDays}-day attendance streak. ${rewardDescription}`,
      tag: 'streak-milestone',
      icon: '/logo192.png',
      requireInteraction: true,
      data: { type: 'streak_milestone', action: '/hr' }
    });
  }

  /**
   * Show salary slip notification
   */
  async showSalarySlipNotification(month) {
    return this.showNotification('Salary Slip Available', {
      body: `Your salary slip for ${month} is now available for download.`,
      tag: `salary-slip-${month}`,
      icon: '/logo192.png',
      data: { type: 'salary_slip', action: '/hr/payroll' }
    });
  }

  /**
   * Show admin alert notification
   */
  async showAdminAlert(title, message, alertType = 'info') {
    return this.showNotification(title, {
      body: message,
      tag: `admin-alert-${Date.now()}`,
      icon: '/logo192.png',
      requireInteraction: alertType === 'critical',
      data: { type: 'admin_alert', alertType }
    });
  }

  /**
   * Show daily attendance summary notification (for admin)
   */
  async showDailySummaryNotification(present, late, absent, onLeave) {
    return this.showNotification('Daily Attendance Summary', {
      body: `Present: ${present} | Late: ${late} | Absent: ${absent} | On Leave: ${onLeave}`,
      tag: 'daily-summary',
      icon: '/logo192.png',
      data: { type: 'daily_summary', action: '/hr' }
    });
  }
}

// Create singleton instance
const notificationService = new NotificationService();

export default notificationService;
